import Phaser from "phaser";
import { ShopSection } from "@phaser/ui/shop/ShopSection";
import {
  getShopCatalog,
  groupShopCatalogByCategory,
  SHOP_CATEGORY_LABELS,
} from "@phaser/core/shop/shopCatalog";
import type { ShopViewItem } from "@phaser/core/shop/shopTypes";
import { shopInventoryManager } from "@phaser/core/shop/ShopInventoryManager";
import { shopEquipManager } from "@phaser/core/shop/ShopEquipManager";

interface ShopScrollViewOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  initialScrollY?: number;
}

export class ShopScrollView extends Phaser.GameObjects.Container {
  private content!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private minScrollY = 0;
  private maxScrollY = 0;
  private viewX: number;
  private viewY: number;
  private viewWidth: number;
  private viewHeight: number;
  private sections: ShopSection[] = [];

  constructor(scene: Phaser.Scene, options: ShopScrollViewOptions) {
    super(scene, options.x, options.y);
    scene.add.existing(this);

    this.viewX = options.x;
    this.viewY = options.y;
    this.viewWidth = options.width;
    this.viewHeight = options.height;

    this.content = scene.add.container(0, 0);
    this.add(this.content);

    const maskGraphics = scene.make.graphics({});
    maskGraphics.fillStyle(0xffffff, 1);
    maskGraphics.fillRect(options.x, options.y, options.width, options.height);
    this.setMask(maskGraphics.createGeometryMask());

    const contentHeight = this.buildSections(scene, options.width);

    this.minScrollY = Math.min(0, options.height - contentHeight);
    this.maxScrollY = 0;

    // 초기 스크롤 위치 복원
    if (options.initialScrollY !== undefined) {
      this.setScroll(options.initialScrollY);
    } else {
      this.setScroll(0);
    }

    this.bindInput(scene);
  }

  private buildSections(scene: Phaser.Scene, width: number) {
    let currentY = 0;
    const sectionGap = 32;

    this.sections = [];

    const sections = groupShopCatalogByCategory(
      getShopCatalog(),
      SHOP_CATEGORY_LABELS,
    );

    sections.forEach((sectionData) => {
      const viewItems: ShopViewItem[] = sectionData.items.map((item) => ({
        ...item,
        owned: shopInventoryManager.isOwned(item.id),
        equipped: shopEquipManager.isEquipped(item.category, item.id),
      }));

      const section = new ShopSection(scene, 0, currentY, {
        width,
        title: sectionData.title,
        items: viewItems,
      });

      this.sections.push(section);
      this.content.add(section);
      currentY += section.contentHeight + sectionGap;
    });

    return currentY;
  }

  private bindInput(scene: Phaser.Scene) {
    scene.input.on(
      "wheel",
      (
        pointer: Phaser.Input.Pointer,
        _gameObjects: Phaser.GameObjects.GameObject[],
        _deltaX: number,
        deltaY: number,
      ) => {
        if (!this.isPointerInside(pointer)) return;
        this.setScroll(this.scrollY - deltaY * 0.8);
      },
    );

    let isDragging = false;
    let dragStartY = 0;
    let startScrollY = 0;

    scene.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!this.isPointerInside(pointer)) return;

      isDragging = true;
      dragStartY = pointer.y;
      startScrollY = this.scrollY;
    });

    scene.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (!isDragging || !pointer.isDown) return;

      const dy = pointer.y - dragStartY;
      this.setScroll(startScrollY + dy);
    });

    scene.input.on("pointerup", () => {
      isDragging = false;
    });
  }

  private isPointerInside(pointer: Phaser.Input.Pointer) {
    return (
      pointer.x >= this.viewX &&
      pointer.x <= this.viewX + this.viewWidth &&
      pointer.y >= this.viewY &&
      pointer.y <= this.viewY + this.viewHeight
    );
  }

  private setScroll(nextY: number) {
    this.scrollY = Phaser.Math.Clamp(nextY, this.minScrollY, this.maxScrollY);
    this.content.y = this.scrollY;
  }

  public getScrollY() {
    return this.scrollY;
  }

  public setScrollY(nextY: number) {
    this.setScroll(nextY);
  }

  public refresh() {
    this.content.removeAll(true);

    const contentHeight = this.buildSections(this.scene, this.viewWidth);

    this.minScrollY = Math.min(0, this.viewHeight - contentHeight);
    this.maxScrollY = 0;

    this.setScroll(this.scrollY);
  }
}
